import {useAuth} from '@apps-common/auth-react';
import * as searchesApi from '@apps-common/js-client/api/searches';
import ErrorPage from '@apps-common/ui-components/ErrorPage.jsx';
import {MapAuthProvider} from '@apps-common/ui-maps/context/MapAuthProvider.jsx';
import {Box, Button, Zoom, useColorScheme} from '@mui/material';
import {
  ErrorBoundary,
  showReportDialog as showSentryReportDialog,
} from '@sentry/react';
import {KEY_DOWN, KEY_LEFT, KEY_RIGHT, KEY_UP} from 'keycode-js';
import {useGeographic as setGeographic} from 'ol/proj';
import PropTypes, {array, bool, func, object, string} from 'prop-types';
import React, {useCallback, useEffect, useState} from 'react';
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import {focusItem} from '../actions/focused-item.js';
import {selectItemTypes} from '../actions/item-types.js';
import {setDrawTool, setOverlayOn, setUserOverlayUrl} from '../actions/map.js';
import {selectMosaic} from '../actions/mosaics.js';
import {loadSearch, searchSingleItem} from '../actions/search.js';
import {setActiveResultId} from '../actions/sidebar.js';
import {hideModal, resize, showModal} from '../actions/ui.js';
import SessionStorageComponent from '../blobz/session-storage-component.js';
import {
  DRAWERS,
  MAP_DRAW_LAYERS,
  MAP_TOOLS,
  MODALS,
  TILE_MODE,
  TOUR_STAGES,
  UI_MODES,
} from '../constants/index.js';
import {selectFilteredResultIds} from '../selectors/results.js';
import pluckQueryParam from '../util/pluck-query-param.js';
import ModalsManager from './dialogs/index.jsx';
import UploadDialog from './dialogs/upload-dialog/index.jsx';
import FileDrop from './filedrop.jsx';
import LeftNav from './navbars/left/index.jsx';
import RightNav from './navbars/right/index.jsx';
import Notifier from './notifications/notifier.jsx';
import SearchInput from './search-input/index.jsx';
import Sidebars from './sidebars/index.jsx';
import Tour from './tour/index.jsx';
import ShiftContainer from './ui/shift-containers.jsx';
import Viewer from './viewer/index.jsx';
import WorkerManager from './worker-manager.js';
import {notify} from '@explorer/actions/notifications';
import {addResultToView} from '@explorer/actions/results';
import {setAccessPermissions} from '@explorer/actions/search-filters.js';
import {trackEvent} from '@explorer/analytics';
import {getItem as getItemRequest} from '@explorer/api/items';
import {MAPBOX_ACCESS_TOKEN} from '@explorer/config.js';
import {getMosaics, getSelectedMosaicName} from '@explorer/selectors/mosaics';
import {showTimeline} from '@explorer/selectors/ui';
import {hydrateSearchGeometry} from '@explorer/util/granero-resolver';
import {isTrialUser} from '@explorer/util/user.js';

// This sets the openlayers userProjection to EPSG:4326
setGeographic();

const SentryReportButton = ({eventId}) => {
  const {user} = useAuth();

  return (
    <Button
      color="primary"
      variant="contained"
      onClick={() => {
        showSentryReportDialog({
          eventId,
          user: {
            name: user?.claims?.name,
            email: user?.claims?.email,
          },
        });
      }}
    >
      Report issue
    </Button>
  );
};

SentryReportButton.propTypes = {
  eventId: PropTypes.string,
};

const App = ({
  resize,
  showModal,
  setUserOverlayUrl,
  setOverlayOn,
  ...props
}) => {
  const auth = useAuth();
  const {t} = useTranslation();
  const {setMode} = useColorScheme();

  const [isDragging, setIsDragging] = useState(false);
  const [inFiles, setInFiles] = useState([]);
  const [isReady, setIsReady] = useState(false);

  const [containerRef, setContainerRef] = useState();

  // https://legacy.reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
  // there might be better ways, this works for now
  const containerRefChanged = useCallback(node => {
    if (node !== null) {
      setContainerRef(node);
    }
  }, []);

  const onResize = useCallback(() => {
    if (containerRef) {
      resize({
        width: containerRef.offsetWidth,
        height: containerRef.offsetHeight,
      });
    }
  }, [containerRef, resize]);

  const getItem = query => {
    const [itemType, itemId] = query.split('::');

    if (!itemType || !itemId) {
      return Promise.resolve(null);
    }

    // we intentionally don't care about bad requests here
    return getItemRequest({itemType, itemId}).catch(() => null);
  };

  //ToDo: Review this...
  const onKeyDown = useCallback(
    event => {
      const tagName = event.target.tagName.toUpperCase();
      const typing =
        (tagName === 'INPUT' || tagName === 'TEXTAREA') && event.target.value;

      // We don't want to modify selections if a modal is open
      if (!typing && !props.modal) {
        // sidebar is open
        const arrowKeys = [KEY_LEFT, KEY_RIGHT];

        if (props.isSidebarOpen) {
          arrowKeys.push(KEY_UP, KEY_DOWN);
        }

        if (arrowKeys.indexOf(event.keyCode) !== -1) {
          event.preventDefault();
          event.stopPropagation();

          const direction =
            event.keyCode === KEY_UP || event.keyCode === KEY_RIGHT ? -1 : 1;

          if (
            props.tileMode === TILE_MODE.RESULTS &&
            props.viewResults.length
          ) {
            const selectedResultIndex =
              direction === -1 ? props.viewResults.length - 1 : 0;
            const selectedResultId = props.viewResults[selectedResultIndex];
            const resultIndex = props.resultIds.indexOf(selectedResultId);

            const nextIndex = resultIndex + direction;
            if (nextIndex >= 0 && nextIndex < props.resultIds.length) {
              const nextResultId = props.resultIds[nextIndex];

              // this can go away if we just save the id
              const nextResult = props.resultsById[nextResultId];

              props.addResultToView({resultId: nextResult.id, reset: true});
              // clear out any sidebar selections to go back to the list
              props.setActiveResultId(null);
              props.focusItem(null);
            }
          } else if (
            props.tileMode === TILE_MODE.BASEMAPS &&
            props.selectedMosaicName
          ) {
            // we're dealing with mosaics
            const mosaicIndex = props.mosaics.findIndex(
              mosaic => mosaic.name === props.selectedMosaicName
            );

            if (mosaicIndex >= 0) {
              const nextMosaic = props.mosaics[mosaicIndex + direction];
              if (nextMosaic) {
                props.selectMosaic(nextMosaic.id);
                trackEvent('Mosaic changed', {
                  keyPress: true,
                  mosaic: nextMosaic.name,
                });
              }
            }
          }
        }
      }
    },
    [props]
  );

  const onDrawClick = () => {
    props.setDrawTool({
      name: MAP_TOOLS.DRAW_LOCKED_RECT,
      layer: MAP_DRAW_LAYERS.GEOMETRY,
    });
  };

  const onDragEnter = event => {
    const types = event.dataTransfer.types;
    for (let i = 0; i < types.length; i++) {
      if (types[i] === 'Files') {
        setIsDragging(true);
        break;
      }
    }
  };

  const onDragLeave = useCallback(() => {
    setIsDragging(false);
  }, [setIsDragging]);

  const onDrop = useCallback(
    event => {
      setIsDragging(false);
      setInFiles([event.dataTransfer.files[0]]);

      showModal(MODALS.UPLOAD);
    },
    [showModal, setIsDragging, setInFiles]
  );

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);

    window.addEventListener(
      'dragover',
      function (e) {
        e.preventDefault();
      },
      false
    );
    window.addEventListener(
      'drop',
      e => {
        e.preventDefault();
        const items = e.dataTransfer.items;
        if (items.length > 0) {
          if (items[0].kind === 'string') {
            // probability that this is a URL.
            items[0].getAsString(itemString => {
              // oh, yeah, definitely a URL
              if (itemString.indexOf('http') === 0) {
                setUserOverlayUrl(itemString);
                setOverlayOn({name: 'user', on: true});
              }
            });
          }
        }
      },
      false
    );

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
    };
  }, [onKeyDown, onResize, setUserOverlayUrl, setOverlayOn]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onResize, [containerRef]);

  useEffect(() => {
    const searchId = pluckQueryParam('search');
    if (searchId) {
      trackEvent('Loaded a saved search from link', {searchId});

      const fetchSearch = async () => {
        try {
          const search = await searchesApi.get(searchId);
          const hydratedSearch = await hydrateSearchGeometry(search);
          props.loadSearch(hydratedSearch);
        } catch (error) {
          props.notify({
            severity: 'error',
            message: t('savedSearch.loadError'),
          });
        }
      };

      fetchSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const queryItemId = pluckQueryParam('item');
    if (queryItemId) {
      const itemId = queryItemId.split(',')[0];
      getItem(itemId).then(item => {
        if (!item) {
          return;
        }
        props.searchSingleItem(item);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    props.setAccessPermissions(!isTrialUser(auth.user.claims));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user.claims]);

  // Force dark mode
  useEffect(() => {
    setMode('dark');
  }, [setMode]);

  return (
    <ErrorBoundary
      fallback={errorProps => (
        <ErrorPage
          reportIssueButton={
            <SentryReportButton eventId={errorProps.eventId} />
          }
        />
      )}
      showDialog
      dialogOptions={{
        user: {
          name: auth.user.claims.name,
          email: auth.user.claims.email,
        },
      }}
    >
      <SessionStorageComponent onReadyChange={setIsReady}>
        <MapAuthProvider
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          authToken={auth.user.accessToken}
        >
          <Box
            sx={{
              display: 'flex',
              flex: 1,
            }}
          >
            <LeftNav />
            <Box
              ref={containerRefChanged}
              onDragEnter={onDragEnter}
              sx={{
                position: 'relative',
                flex: 1,
                bgcolor: '#2C2C2B',
              }}
            >
              {isReady && (
                <DndProvider backend={HTML5Backend}>
                  <Viewer
                    showTimeline={props.showTimeline}
                    leftDrawer={props.leftDrawer}
                    rightDrawer={props.rightDrawer}
                    onSearchHere={onDrawClick}
                    isComparing={props.mode === UI_MODES.COMPARE}
                    compareMethod={props.compareMethod}
                    showSnapshotLab={props.mode === UI_MODES.SNAPSHOT}
                  />
                  <React.Fragment>
                    <ShiftContainer
                      left={8}
                      position="absolute"
                      top={8}
                      width={props.showSearchBar ? 392 : 0}
                    >
                      <Zoom in={props.showSearchBar} timeout={300}>
                        <div data-tour={TOUR_STAGES.SEARCH_INPUT}>
                          <SearchInput />
                        </div>
                      </Zoom>
                    </ShiftContainer>
                    <Sidebars />
                  </React.Fragment>
                </DndProvider>
              )}
            </Box>
            <RightNav />
          </Box>

          <WorkerManager />
          <Notifier />
          <ModalsManager />
          <UploadDialog inFiles={inFiles} onClose={props.hideModal} />
          <FileDrop
            isDragging={isDragging}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          />
          <Tour />
        </MapAuthProvider>
      </SessionStorageComponent>
    </ErrorBoundary>
  );
};

App.propTypes = {
  isSidebarOpen: bool.isRequired,
  mode: string.isRequired,
  mosaics: array,
  resultIds: array,
  resultsById: object,
  selectedMosaicName: string,
  showSearchBar: bool,
  showTimeline: bool,
  snapshotMode: bool,
  viewResults: array.isRequired,
  leftDrawer: string,
  rightDrawer: string,

  // dispatch actions
  addResultToView: func,
  focusItem: func,
  hideModal: func,
  loadSearch: func,
  modal: string,
  notify: func,
  resize: func,
  searchSingleItem: func,
  selectItemTypes: func,
  selectMosaic: func,
  setActiveResultId: func,
  setDrawTool: func,
  setOverlayOn: func,
  setUserOverlayUrl: func,
  showModal: func,
  tileMode: string,
  compareMethod: string,
  setAccessPermissions: func,
};

const mapStateToProps = state => ({
  leftDrawer: state.ui.drawers.left,
  rightDrawer: state.ui.drawers.right,
  isSidebarOpen:
    state.ui.drawers.left === DRAWERS.SEARCH ||
    state.ui.drawers.left === DRAWERS.BASEMAPS,
  modal: state.ui.modal,
  mode: state.ui.mode,
  resultIds: selectFilteredResultIds(state),
  resultsById: state.results.resultsById,
  selectedMosaicName: getSelectedMosaicName(state),
  mosaics: getMosaics(state),
  snapshotMode: state.ui.snapshotMode,
  tileMode: state.tiles.mode,
  showTimeline: showTimeline(state),
  showSearchBar: state.ui.mode !== UI_MODES.SNAPSHOT,
  viewResults: state.results.viewResults,
  compareMethod: state.compare.method,
});

const mapDispatchToProps = {
  addResultToView,
  focusItem,
  hideModal,
  loadSearch,
  notify,
  resize,
  searchSingleItem,
  selectItemTypes,
  selectMosaic,
  setActiveResultId,
  setDrawTool,
  setOverlayOn,
  setUserOverlayUrl,
  showModal,
  setAccessPermissions,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
